import { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Layout } from '../../components/Layout';
import Content from './Content';
import { Tabs } from './types';
import {
  selectSubscriptionSummaries,
  selectActiveCluster,
  setActiveClusterId,
} from '../../store/slices/subscription';
import {
  useGetSubscriptionSummariesByUserIdQuery,
  useGetAllAvailableBalancesFromExchangesQuery,
} from '../../store/services/api';
import { selectUserId } from '../../store/slices/user';
import useWidthBreakpoint from '../../hooks/useWidthBreakpoint';
import { Exchange } from 'grts-utils/types/Exchange';
import './index.css';

const Balance = () => {
  const dispatch = useDispatch();
  const userId = useSelector(selectUserId);

  useGetSubscriptionSummariesByUserIdQuery(userId, {
    refetchOnMountOrArgChange: true,
  });

  const { data: availableBalancesData } =
    useGetAllAvailableBalancesFromExchangesQuery(userId, {
      refetchOnMountOrArgChange: true,
    });

  const data = useSelector(selectSubscriptionSummaries);
  const activeCluster = useSelector(selectActiveCluster);
  const { isMatch } = useWidthBreakpoint();

  const [activeTab, setActiveTab] = useState(Tabs.ManualDistribution);
  const [selectedExchange, setSelectedExchange] = useState<Exchange | null>(
    null,
  );

  const activeClusterSubscriptions = useMemo(() => {
    return data
      .filter((s) =>
        activeCluster === null ? true : s.clusterId === activeCluster,
      )
      .sort((a, b) => b.totalBalanceInStable - a.totalBalanceInStable);
  }, [activeCluster, data]);

  const exchanges = useMemo(() => {
    const exchangeSet = new Set(
      activeClusterSubscriptions.map((s) => s.exchange),
    );
    return Array.from(exchangeSet);
  }, [activeClusterSubscriptions]);

  // Auto-select first exchange when list changes
  useMemo(() => {
    if (
      exchanges.length > 0 &&
      (!selectedExchange || !exchanges.includes(selectedExchange))
    ) {
      setSelectedExchange(exchanges[0]);
    }
  }, [exchanges, selectedExchange]);

  const totalValueByCluster = useMemo(() => {
    if (!data.length) return {};
    return data.reduce<Record<string, number>>((acc, sub) => {
      acc[sub.clusterId] = (acc[sub.clusterId] || 0) + sub.totalBalanceInStable;
      return acc;
    }, {});
  }, [data]);

  const entirePortfolioValue = useMemo(() => {
    return Object.values(totalValueByCluster).reduce(
      (acc, curr) => acc + curr,
      0,
    );
  }, [totalValueByCluster]);

  const costBasisByCluster = useMemo(() => {
    if (!data.length) return {};
    return data.reduce<Record<string, number>>((acc, sub) => {
      acc[sub.clusterId] =
        (acc[sub.clusterId] || 0) + (sub.profits?.costBasis.stable || 0);
      return acc;
    }, {});
  }, [data]);

  const totalStableProfitByCluster = useMemo(() => {
    const clusterIds = Object.keys(totalValueByCluster);
    return clusterIds.reduce<Record<string, number>>((acc, curr) => {
      acc[curr] =
        (totalValueByCluster[curr] - costBasisByCluster[curr]) /
        costBasisByCluster[curr];
      return acc;
    }, {});
  }, [costBasisByCluster, totalValueByCluster]);

  const entirePortfolioProfit = useMemo(() => {
    const totalCostBasis = Object.values(costBasisByCluster).reduce(
      (acc, curr) => acc + curr,
      0,
    );
    if (totalCostBasis === 0) return 0;
    return (entirePortfolioValue - totalCostBasis) / totalCostBasis;
  }, [costBasisByCluster, entirePortfolioValue]);

  const actions = [Tabs.ManualDistribution, Tabs.DepositDistribution];

  const hasData = !!Object.keys(totalValueByCluster).length;
  const hasMultipleClusters = Object.keys(totalValueByCluster).length > 1;

  const activeClusterItem = useMemo(() => {
    if (activeCluster === null) {
      return (
        <Layout.SideMenuItem
          id={'Portfolio'}
          label={'Portfolio'}
          value={entirePortfolioValue}
          delta={entirePortfolioProfit}
        />
      );
    }
    return (
      <Layout.SideMenuItem
        key={activeCluster}
        id={activeCluster}
        label={activeCluster}
        value={totalValueByCluster[activeCluster]}
        delta={totalStableProfitByCluster[activeCluster]}
      />
    );
  }, [
    activeCluster,
    entirePortfolioProfit,
    entirePortfolioValue,
    totalStableProfitByCluster,
    totalValueByCluster,
  ]);

  const clusterOptionsItems = useMemo(() => {
    if (activeCluster === null) {
      return Object.entries(totalValueByCluster).map(([clusterId, value]) => (
        <Layout.SideMenuItem
          key={clusterId}
          id={clusterId}
          label={clusterId}
          value={value}
          delta={totalStableProfitByCluster[clusterId]}
          onClick={(clusterId: string) =>
            dispatch(setActiveClusterId(clusterId))
          }
          isActive={activeCluster === clusterId}
        />
      ));
    }
    return [
      <Layout.SideMenuItem
        key={'Portfolio'}
        id={'Portfolio'}
        label={'Portfolio'}
        value={entirePortfolioValue}
        delta={entirePortfolioProfit}
        onClick={() => dispatch(setActiveClusterId(null))}
      />,
      ...Object.entries(totalValueByCluster)
        .filter(([clusterId]) => clusterId !== activeCluster)
        .map(([clusterId, value]) => (
          <Layout.SideMenuItem
            key={clusterId}
            id={clusterId}
            label={clusterId}
            value={value}
            delta={totalStableProfitByCluster[clusterId]}
            onClick={(clusterId: string) =>
              dispatch(setActiveClusterId(clusterId))
            }
            isActive={activeCluster === clusterId}
          />
        )),
    ];
  }, [
    activeCluster,
    dispatch,
    entirePortfolioProfit,
    entirePortfolioValue,
    totalStableProfitByCluster,
    totalValueByCluster,
  ]);

  return (
    <Layout>
      {hasData ? (
        <Layout.SideMenu>
          <>
            <Layout.SideMenuSection>
              {isMatch('md') ? (
                <>
                  {hasMultipleClusters ? (
                    <>
                      <Layout.SideMenuTitle
                        title="Clusters"
                        count={Object.keys(totalValueByCluster).length}
                      />
                      <Layout.SideMenuItem
                        id={'Portfolio'}
                        label={'Portfolio'}
                        value={entirePortfolioValue}
                        delta={entirePortfolioProfit}
                        big
                        onClick={() => dispatch(setActiveClusterId(null))}
                        isActive={activeCluster === null}
                      />
                    </>
                  ) : (
                    <Layout.SideMenuTitle title="Portfolio" />
                  )}
                  <>
                    {Object.entries(totalValueByCluster).map(
                      ([clusterId, value]) => (
                        <Layout.SideMenuItem
                          key={clusterId}
                          id={clusterId}
                          label={clusterId}
                          value={value}
                          delta={totalStableProfitByCluster[clusterId]}
                          big
                          onClick={(clusterId: string) =>
                            dispatch(setActiveClusterId(clusterId))
                          }
                          isActive={activeCluster === clusterId}
                        />
                      ),
                    )}
                  </>
                </>
              ) : (
                <>
                  <Layout.SideMenuTitle title="Active Cluster" />
                  <Layout.SideMenuSelect
                    activeItem={activeClusterItem}
                    options={clusterOptionsItems}
                  />
                </>
              )}
            </Layout.SideMenuSection>

            <Layout.SideMenuSection>
              <Layout.SideMenuTitle title="Actions" count={actions.length} />
              <>
                {actions.map((action) => (
                  <Layout.SideMenuItem
                    key={action}
                    id={action}
                    label={action}
                    onClick={setActiveTab}
                    isActive={activeTab === action}
                  />
                ))}
              </>
            </Layout.SideMenuSection>
          </>
        </Layout.SideMenu>
      ) : undefined}
      <Layout.Content>
        <Content
          activeTab={activeTab}
          subscriptions={activeClusterSubscriptions}
          exchanges={exchanges}
          selectedExchange={selectedExchange}
          onExchangeChange={setSelectedExchange}
          availableBalances={availableBalancesData?.balances}
        />
      </Layout.Content>
    </Layout>
  );
};

export default Balance;
